import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { Icon } from '@sdb/planning-ui';

import { GridCompact12 } from 'components/shared/Grid/Grid.components';
import SectionHeader from 'components/mrp/components/AppointmentForm/components/SectionHeader/SectionHeader';
import EntitySearch from 'components/mrp/components/EntitySearch';
import { Popover } from 'components/mrp/components/Popover';
import { EntitySearchInput } from 'components/mrp/components/EntitySearch/components/EntitySearchInput';
import ClickHandler from 'components/mrp/components/Popover/components/ClickHandler/ClickHandler';
import { getEntityIcon } from 'components/mrp/mrp.utils';

import { PlanningEntityIdentifier, PlanningEntityType } from 'generated/index';

import {
  ParticipantActionButton,
  SelectedParticipantRow
} from './EditParticipantsDialog.styles';

export type ExistingParticipant = {
  entityId: string;
  label: string;
  number?: string;
};

export type AddedParticipant = {
  entity: PlanningEntityIdentifier;
  label: string;
};

type EditParticipantsEntitySectionProps = {
  planningEntityType: PlanningEntityType;
  existingParticipants: ExistingParticipant[];
  removedEntityIds: Set<string>;
  addedParticipants: AddedParticipant[];
  cancelledAddedIds: Set<string>;
  onToggleRemove: (entityId: string) => void;
  onAddEntity: (entity: PlanningEntityIdentifier, label: string) => void;
  onToggleCancelAdded: (entityId: string) => void;
};

const EditParticipantsEntitySection = ({
  planningEntityType,
  existingParticipants,
  removedEntityIds,
  addedParticipants,
  cancelledAddedIds,
  onToggleRemove,
  onAddEntity,
  onToggleCancelAdded
}: EditParticipantsEntitySectionProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState('');
  const dummyInputRef = useRef<HTMLInputElement | null>(null);
  const prevOpenState = useRef(isPopoverOpen);

  const closePopover = useCallback(() => {
    setIsPopoverOpen(false);
  }, []);

  // Refocus input after popover closes
  useEffect(() => {
    if (prevOpenState.current && !isPopoverOpen) {
      dummyInputRef.current?.focus();
    }
    prevOpenState.current = isPopoverOpen;
  }, [isPopoverOpen]);

  const handleEntityChange = useCallback(
    (entity: PlanningEntityIdentifier, label: string) => {
      onAddEntity(entity, label);
      closePopover();
    },
    [onAddEntity, closePopover]
  );

  return (
    <GridCompact12>
      <SectionHeader
        label={t(`entityTypeMap.${planningEntityType}.plural`)}
        icon={getEntityIcon(planningEntityType, undefined, 'MEDIUM')}
      />

      <Popover
        id={`edit-participants-search-${planningEntityType}`}
        forceComponentWidth
        useTransition={false}
        useForcedState={true}
        forceOpen={isPopoverOpen}
        onClose={closePopover}
        component={
          <EntitySearch
            filter={[planningEntityType]}
            minWidth={250}
            mode="compact"
            onChange={handleEntityChange}
            onEscape={closePopover}
            initialQuery={initialQuery}
          />
        }
      >
        <ClickHandler
          onClick={() => {
            setInitialQuery('');
            setIsPopoverOpen(true);
          }}
        >
          <EntitySearchInput
            ref={dummyInputRef}
            useAsDummy
            placeholder={
              t('searchEntity') +
              t(`entityTypeMap.${planningEntityType}.plural`).toLowerCase()
            }
            onEnterPress={() => setIsPopoverOpen(true)}
            onCharacterInput={(character) => {
              setInitialQuery(character);
              setIsPopoverOpen(true);
            }}
          />
        </ClickHandler>
      </Popover>

      {existingParticipants.map((p) => {
        const removed = removedEntityIds.has(p.entityId);
        return (
          <SelectedParticipantRow key={p.entityId} markedForRemoval={removed}>
            {p.label}

            <Box>
              <ParticipantActionButton
                variant="text"
                onClick={() => onToggleRemove(p.entityId)}
              >
                <Icon type={removed ? 'plus' : 'xmark'} fontSize="inherit" />
              </ParticipantActionButton>
            </Box>
          </SelectedParticipantRow>
        );
      })}

      {addedParticipants.map((p) => {
        const cancelled = cancelledAddedIds.has(p.entity.entityId!);
        return (
          <SelectedParticipantRow
            key={p.entity.entityId}
            markedForRemoval={cancelled}
          >
            {p.label}

            <Box>
              <ParticipantActionButton
                variant="text"
                onClick={() => onToggleCancelAdded(p.entity.entityId!)}
              >
                <Icon type={cancelled ? 'plus' : 'xmark'} fontSize="inherit" />
              </ParticipantActionButton>
            </Box>
          </SelectedParticipantRow>
        );
      })}
    </GridCompact12>
  );
};

export default EditParticipantsEntitySection;
